'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PaymentDetails } from '@/lib/api/payments';
import { formatMoney } from "@/lib/money";

interface PaymentRefundDialogProps {
  payment: PaymentDetails;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (params: { refundType: 'full' | 'partial'; amount?: number; cancelSubscription: boolean }) => void;
  isProcessing: boolean;
}

export function PaymentRefundDialog({
  payment,
  open,
  onOpenChange,
  onConfirm,
  isProcessing,
}: PaymentRefundDialogProps) {
  const { t } = useTranslation('payments');
  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
  const [customAmount, setCustomAmount] = useState('');
  const [cancelSubscription, setCancelSubscription] = useState(false);

  const hasSubscription = !!(payment as any).subscriptionId;

  const totalAmount = payment.amount || 0;
  const refundedAmount = payment.refundedAmount || 0;
  const availableToRefund = totalAmount - refundedAmount;
  const availableToRefundDecimal = availableToRefund / 100;

  const refundAmount =
    refundType === 'full' ? availableToRefundDecimal : Number.parseFloat(customAmount) || 0;

  const handleRefund = () => {
    if (refundType === 'partial') {
      const amount = Math.round(refundAmount * 100); // Convert to minor units
      onConfirm({ refundType: 'partial', amount, cancelSubscription });
    } else {
      // For full refund, pass the remaining refundable amount
      onConfirm({ refundType: 'partial', amount: availableToRefund, cancelSubscription });
    }
  };

  // Get currency symbol
  const getCurrencySymbol = (currency: string) => {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
    };
    return symbols[currency] || currency;
  };

  const currencySymbol = getCurrencySymbol(payment.currency || 'USD');

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) setCancelSubscription(false); onOpenChange(val); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('refundPayment')}</DialogTitle>
          <DialogDescription>{t('refundDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Payment Summary */}
          <div className="bg-muted/50 space-y-3 rounded-lg p-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('refundDialog.paymentAmount')}</span>
              <span className="font-medium">{formatMoney(totalAmount, payment.currency)}</span>
            </div>

            {refundedAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('refundDialog.alreadyRefunded')}</span>
                <span className="font-medium">{formatMoney(refundedAmount, payment.currency)}</span>
              </div>
            )}

            <div className="flex justify-between border-t pt-2 text-sm font-semibold">
              <span>{t('refundDialog.availableToRefund')}</span>
              <span>{formatMoney(availableToRefund, payment.currency)}</span>
            </div>
          </div>

          {/* Refund Options */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t('refundDialog.refundType')}</Label>

            <RadioGroup
              value={refundType}
              onValueChange={(value) => setRefundType(value as 'full' | 'partial')}
              className="space-y-3"
            >
              <div className="hover:bg-muted/50 flex items-center justify-between rounded-lg border p-4 transition-colors">
                <div className="flex items-center space-x-3">
                  <RadioGroupItem
                    value="full"
                    id="full"
                    className="data-[state=checked]:border-primary data-[state=checked]:bg-primary border-2 border-gray-300 bg-white"
                  />
                  <Label htmlFor="full" className="cursor-pointer font-medium">
                    {t('refundDialog.fullRefund')}
                  </Label>
                </div>
                <span className="text-sm font-medium">
                  {formatMoney(availableToRefund, payment.currency)}
                </span>
              </div>

              {payment.subStatus !== 'authorized' && (
                <div className="hover:bg-muted/50 rounded-lg border p-4 transition-colors">
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem
                      value="partial"
                      id="partial"
                      className="data-[state=checked]:border-primary data-[state=checked]:bg-primary border-2 border-gray-300 bg-white"
                    />
                    <Label htmlFor="partial" className="cursor-pointer font-medium">
                      {t('refundDialog.partialRefund')}
                    </Label>
                  </div>

                  {refundType === 'partial' && (
                    <div className="ml-7 mt-3 space-y-2">
                      <Label htmlFor="custom-amount" className="text-muted-foreground text-sm">
                        {t('refundDialog.customAmount')}
                      </Label>
                      <div className="relative">
                        <span className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2">
                          {currencySymbol}
                        </span>
                        <Input
                          id="custom-amount"
                          type="number"
                          placeholder="0.00"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          className="pl-8"
                          step="0.01"
                          min="0"
                          max={availableToRefundDecimal}
                        />
                      </div>
                      <p className="text-muted-foreground text-xs">
                        {t('refundDialog.maximum', { amount: formatMoney(availableToRefund, payment.currency) })}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </RadioGroup>
          </div>

          {/* Cancel Subscription */}
          {hasSubscription && (
            <div className="flex items-center justify-between rounded-lg border p-4">
              <Label htmlFor="cancel-subscription" className="flex flex-col space-y-1 cursor-pointer">
                <span className="font-medium">{t('refundDialog.cancelSubscription')}</span>
                <span className="text-muted-foreground text-sm font-normal">
                  {t('refundDialog.cancelSubscriptionDescription')}
                </span>
              </Label>
              <Switch
                id="cancel-subscription"
                checked={cancelSubscription}
                onCheckedChange={setCancelSubscription}
              />
            </div>
          )}

          {/* Refund Summary */}
          <div className="bg-card rounded-lg border p-4">
            <div className="flex justify-between font-semibold">
              <span>{t('refundDialog.refundAmount')}</span>
              <span>{formatMoney(Math.round(refundAmount * 100), payment.currency)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleRefund}
            disabled={
              isProcessing ||
              (refundType === 'partial' &&
                (!customAmount || refundAmount <= 0 || refundAmount > availableToRefundDecimal))
            }
          >
            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {refundType === 'full' ? t('refundDialog.refundFullAmount') : t('refundDialog.refundPartialAmount')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
